/**
 * The `/v1/calls` REST surface.
 *
 * A call is not a meeting: there is no lobby and no scheduling, so every call
 * verb here is a single round trip that either hands back a media grant or
 * changes one participant's state.
 */
import type {
  AnswerCallResponse, Call, CallHistoryEntry, CallKind, CallSettings, PlaceCallResponse, Voicemail,
} from '@cyphex/types/calls';
import type { CompleteUploadRequest, CreateUploadRequest, UploadSession } from '@cyphex/types/files';
import type { ConversationId, UserRef } from '@cyphex/types';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api/client';

/** Who a message left on this call would reach, and whether one may be left. */
export interface VoicemailEligibility {
  readonly canLeave: boolean;
  readonly recipient: UserRef | null;
  readonly reason: string | null;
}

export type TransferMode = 'blind' | 'consultative';

/**
 * What `POST /calls/:id/participants` really answers.
 *
 * It was typed `Promise<Call>` and the server has always returned
 * `{ added, call }`. `added` EXCLUDES anybody the ring planner refused — the
 * person who is already on another call, the person whose row on this call
 * already exists — so the client was throwing away the only field that says
 * whether a phone rang, and toasting "Ringing them now." either way.
 */
export interface AddParticipantsResult {
  /** How many phones actually started ringing. Zero is a real answer. */
  readonly added: number;
  readonly call: Call;
}

/** What `POST /calls/:id/escalate` answers. */
export interface EscalationStarted {
  readonly meetingId: string;
  /**
   * The meeting's join code.
   *
   * The escalating client does not need it to READ its own meeting, but the
   * response knows it and a client that has to retry should not have to ask a
   * second endpoint for the one credential this one already had.
   */
  readonly joinCode: string;
}

export interface TransferInput {
  readonly mode: TransferMode;
  readonly targetUserId: string;
}

export interface TransferStarted {
  readonly transferId: string;
  readonly mode: TransferMode;
  /** The second leg a consultative transfer places. `null` for a blind one. */
  readonly consultCallId: string | null;
}

/** One of my other live calls, as the merge picker needs it. */
export interface ActiveCall {
  readonly callId: string;
  readonly isGroup: boolean;
  readonly onHold: boolean;
  readonly others: UserRef[];
}

export interface PlaceCallInput {
  readonly calleeIds: readonly string[];
  readonly kind: CallKind;
  readonly conversationId?: string;
}

export interface CallHistoryFilter {
  readonly direction?: 'incoming' | 'outgoing';
  readonly missedOnly?: boolean;
}

const historyQuery = (filter: CallHistoryFilter): string => {
  const params = new URLSearchParams();
  if (filter.direction !== undefined) params.set('direction', filter.direction);
  if (filter.missedOnly === true) params.set('missedOnly', 'true');
  const text = params.toString();
  return text.length > 0 ? `?${text}` : '';
};

export const callsApi = {
  place: (input: PlaceCallInput): Promise<PlaceCallResponse> =>
    apiPost<PlaceCallResponse>('/calls', {
      calleeIds: [...input.calleeIds],
      kind: input.kind,
      clientCallId: crypto.randomUUID(),
      ...(input.conversationId === undefined ? {} : { conversationId: input.conversationId }),
    }),

  answer: (callId: string, withVideo: boolean): Promise<AnswerCallResponse> =>
    apiPost<AnswerCallResponse>(`/calls/${callId}/answer`, { withVideo }),

  /**
   * Re-mint a grant for a call already answered. `answer` only works once — it
   * moves a RINGING participant to connected — so this is what makes a reload,
   * a second tab and a token expiry survivable.
   */
  join: (callId: string): Promise<AnswerCallResponse> =>
    apiPost<AnswerCallResponse>(`/calls/${callId}/join`, {}),

  hangup: (callId: string): Promise<{ ok: boolean }> =>
    apiPost<{ ok: boolean }>(`/calls/${callId}/hangup`, {}),

  hold: (callId: string, hold: boolean): Promise<{ onHold: boolean }> =>
    apiPost<{ onHold: boolean }>(`/calls/${callId}/hold`, { hold }),

  detail: (callId: string): Promise<Call> => apiGet<Call>(`/calls/${callId}`),

  /**
   * Write the call's resolved chat thread back to the call, and answer with
   * whoever won.
   *
   * The server only accepts the FIRST resolution — the UPDATE is conditional on
   * `conversation_id IS NULL` — so two parties resolving at the same moment
   * both get the same id back and neither can overwrite the other. Callers must
   * therefore use what comes back, not what they sent.
   */
  linkConversation: (
    callId: string,
    conversationId: ConversationId,
  ): Promise<{ conversationId: ConversationId | null }> =>
    apiPost<{ conversationId: ConversationId | null }>(`/calls/${callId}/conversation`, {
      conversationId,
    }),

  history: (filter: CallHistoryFilter = {}): Promise<{ calls: CallHistoryEntry[] }> =>
    apiGet<{ calls: CallHistoryEntry[] }>(`/calls/history${historyQuery(filter)}`),

  voicemail: (): Promise<{ voicemails: Voicemail[] }> =>
    apiGet<{ voicemails: Voicemail[] }>('/calls/voicemail'),

  /* ---- Voicemail write path -------------------------------------------- */

  /**
   * May I leave a message on this call? Asked BEFORE the recorder is offered,
   * so the button never appears for a callee who takes no voicemail, a call
   * that was answered, or one that already has a message on it.
   */
  voicemailEligibility: (callId: string): Promise<VoicemailEligibility> =>
    apiGet<VoicemailEligibility>(`/calls/${callId}/voicemail`),

  /** The recording is already in object storage; this posts its file id. */
  leaveVoicemail: (
    callId: string,
    input: { fileId: string; durationMs: number; waveform: number[] },
  ): Promise<{ voicemail: Voicemail }> =>
    apiPost<{ voicemail: Voicemail }>(`/calls/${callId}/voicemail`, input),

  markVoicemailRead: (voicemailId: string, isRead: boolean): Promise<{ voicemail: Voicemail }> =>
    apiPatch<{ voicemail: Voicemail }>(`/calls/voicemail/${voicemailId}`, { isRead }),

  deleteVoicemail: (voicemailId: string): Promise<{ ok: boolean }> =>
    apiDelete<{ ok: boolean }>(`/calls/voicemail/${voicemailId}`),

  /** Opening Calls: missed calls up to now stop counting on the rail badge. */
  markMissedSeen: (): Promise<{ ok: boolean }> => apiPost<{ ok: boolean }>('/calls/missed/seen', {}),

  /* ---- Upload, for the voicemail recorder ------------------------------- */

  createUpload: (body: CreateUploadRequest): Promise<UploadSession> =>
    apiPost<UploadSession>('/files/uploads', body),

  completeUpload: (sessionId: string, body: CompleteUploadRequest): Promise<unknown> =>
    apiPost<unknown>(`/files/uploads/${sessionId}/complete`, body),

  /* ---- In-call actions -------------------------------------------------- */

  /**
   * Hand the call over. `blind` rings the target and drops you out at once;
   * `consultative` places a second call you can speak on first, and must then
   * be finished with `completeTransfer` or thrown away with `cancelTransfer`.
   */
  transfer: (callId: string, input: TransferInput): Promise<TransferStarted> =>
    apiPost<TransferStarted>(`/calls/${callId}/transfer`, {
      mode: input.mode,
      targetUserId: input.targetUserId,
    }),

  completeTransfer: (callId: string, transferId: string): Promise<{ status: string }> =>
    apiPost<{ status: string }>(`/calls/${callId}/transfer/${transferId}/complete`, {}),

  cancelTransfer: (callId: string, transferId: string): Promise<{ status: string }> =>
    apiPost<{ status: string }>(`/calls/${callId}/transfer/${transferId}/cancel`, {}),

  /** Pull more people into a call already in progress. */
  addParticipants: (callId: string, userIds: readonly string[]): Promise<AddParticipantsResult> =>
    apiPost<AddParticipantsResult>(`/calls/${callId}/participants`, { userIds: [...userIds] }),

  /**
   * Turn the call into a meeting.
   *
   * The call ENDS and everybody who was on it is taken across — the server
   * emits `call.escalated` carrying the roster and the join code, and
   * `useEscalatedCall` is what follows it. See features/calls/lib/escalation.ts.
   */
  escalate: (callId: string, title?: string): Promise<EscalationStarted> =>
    apiPost<EscalationStarted>(`/calls/${callId}/escalate`, {
      ...(title === undefined ? {} : { title }),
    }),

  /** My OTHER live calls — the only calls a merge could name. */
  activeCalls: (exclude: string): Promise<{ calls: ActiveCall[] }> =>
    apiGet<{ calls: ActiveCall[] }>(`/calls/active?exclude=${encodeURIComponent(exclude)}`),

  /** Fold `otherCallId` into this call. The other call ends. */
  merge: (callId: string, otherCallId: string): Promise<{ endedCallId: string }> =>
    apiPost<{ endedCallId: string }>(`/calls/${callId}/merge`, { otherCallId }),

  settings: (): Promise<CallSettings> => apiGet<CallSettings>('/calls/settings'),

  saveSettings: (patch: Partial<CallSettings>): Promise<{ ok: boolean }> =>
    apiPut<{ ok: boolean }>('/calls/settings', patch),

  /** Directory lookup for click-to-call. */
  searchPeople: (query: string): Promise<{ users: UserRef[] }> =>
    apiGet<{ users: UserRef[] }>(
      `/users/search?q=${encodeURIComponent(query)}&excludeSelf=true&limit=8`,
    ),
};
